
import React, { useEffect, useMemo, useRef, useState } from "react"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import CloseIcon from "@mui/icons-material/Close"
import { Box, Button, Checkbox, Chip, Dialog, DialogContent, DialogTitle, FormControlLabel, FormGroup, IconButton, InputAdornment, TextField } from "@mui/material"
import { COLORS } from "../../utils"
import SearchIcon from "@mui/icons-material/Search"
import { useSelector } from "react-redux"
import { getScreen } from "../../reducers/Utils/app"


const TypeFilter = ({
  fields = [],
  value = {},
  onChange,
}) => {

  const screen = useSelector(getScreen)
  const [opened, setOpened] = useState("")
  const [textSearch, setTextSearch] = useState("")
  const optionsLength = useRef(0)

  const buildInitialSelected = (fields, value) =>
    Object.fromEntries(fields.map(f => [f.value, value[f.value] ?? []]))

  const [selectedOptions, setSelectedOptions] = useState(() => buildInitialSelected(fields, value))

  useEffect(() => {
    setSelectedOptions(buildInitialSelected(fields, value))
  }, [value])

  const allValues = useMemo(() => {
    return Object.keys(selectedOptions)
      .flatMap(key => selectedOptions[key].map(v => ({ value: v, type: key })))
      .map(item => {
        const field = fields.find(f => f.value === item.type)
        const label = field?.options?.find(o => o.value === item.value)?.label || item.value
        return { ...item, label }
      })
  }, [selectedOptions, fields])

  const [preselectedOptions, setPreselectedOptions] = useState([])

  const toggleOption = (optionValue) => {
    setPreselectedOptions((prev) => {
      const isSelected = prev.includes(optionValue)
      return isSelected ? prev.filter(v => v !== optionValue) : [...prev, optionValue]
    })
  }

  const clearSelections = (field) => {
    setSelectedOptions(prev => ({ ...prev, [field]: [] }))
  }

  const fieldOptions = useMemo(() => {
    const options = fields.find(f => f.value === opened)?.options || []
    optionsLength.current = options.length
    return options.filter(option => option.label.toLowerCase().indexOf(textSearch.toLowerCase()) > -1)
  }, [opened, textSearch, fields])

  const handleOpen = (field) => {
    setPreselectedOptions(field ? selectedOptions[field] ?? [] : [])
    setTextSearch("")
    setOpened(field)
  }

  const handleApply = () => {
    if (!opened) return
    const currentSelections = { ...selectedOptions }
    setSelectedOptions(prev => ({ ...prev, [opened]: preselectedOptions }))
    onChange && onChange({ ...currentSelections, [opened]: preselectedOptions })
    setOpened("")
  }

  const handleClearAll = () => {
    const clearedOptions = Object.fromEntries(fields.map(f => [f.value, []]))
    setSelectedOptions(clearedOptions)
    onChange && onChange(clearedOptions)
  }

  const handleDeleteChip = (item) => {
    const updatedOptions = selectedOptions[item.type].filter(v => v !== item.value)
    const newSelectedOptions = { ...selectedOptions, [item.type]: updatedOptions }
    setSelectedOptions(newSelectedOptions)
    onChange && onChange(newSelectedOptions)
  }

  return (
    <>
      <Box sx={styles.filtersRow}>
        {fields.filter((field) => field.options && field.options.length > 0).map((field) => (
          <Button
            sx={styles.buttonStyle}
            key={field.value}
            variant="outlined"
            onClick={() => handleOpen(field.value)}
          >
            {field.label}
            {!!selectedOptions[field.value]?.length && <div style={styles.dialogTitleCount}  >{selectedOptions[field.value].length}</div>}
            <ExpandMoreIcon />
          </Button>

        ))}
      </Box>
      {allValues.length > 0 && <Box sx={styles.filtersRow}>
        {allValues.map((item) => {
          return (
            <Chip
              sx={styles.chipStyle}
              key={`${item.type}-${item.value}`}
              label={`${item.label}`}
              onDelete={() => handleDeleteChip(item)}
              deleteIcon={<CloseIcon size="small" sx={{ color: `${COLORS.WHITE} !important` }} />}
            />
          )
        })}
        <Button onClick={handleClearAll}>
          Tout Effacer
        </Button>
      </Box>}
      <Dialog
        open={Boolean(opened)}
        onClose={() => setOpened("")}
        PaperProps={{
          sx: screen !== "desktop" ? styles.paperDialog : { maxWidth: "90%", width: "800px", margin: "20px", borderRadius: "24px" }
        }}
        onClick={(evt) => evt.stopPropagation()}>
        <DialogTitle sx={styles.dialogTitle}>
          {fields.find(f => f.value === opened)?.label}
          {!!preselectedOptions.length && <div style={styles.dialogTitleCount}  >{preselectedOptions.length}</div>}
        </DialogTitle>
        <IconButton
          aria-label="close"
          sx={styles.iconDelete}
          onClick={() => setOpened("")}
        >
          <CloseIcon sx={{ fontSize: "32px" }} />
        </IconButton>
        <DialogContent sx={styles.DialogContent}>
          <TextField
            placeholder={"Rechercher un " + fields.find(f => f.value === opened)?.label.toLowerCase()}
            size="small"
            fullWidth
            sx={styles.searchField}
            value={textSearch}
            onChange={(e) => setTextSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: COLORS.BLACK_500 }} />
                </InputAdornment>
              ),
            }}
          />
          <div style={styles.formGroupContainer}>
            <FormGroup sx={{ minHeight: 53 * optionsLength.current }}>
              {fieldOptions.map((option) => (
                <FormControlLabel key={option.value} control={<Checkbox size="large" checked={preselectedOptions.includes(option.value) || false} onChange={() => toggleOption(option.value)} />} label={option.label} />
              ))}
            </FormGroup>
          </div>
          <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
            {preselectedOptions.length > 0 && <Button
              onClick={() => { setOpened(""); clearSelections(opened) }} color="primary"
              variant="outlined"
              sx={{ marginRight: "24px" }}
            >
              Tout Effacer
            </Button>}
            <Button onClick={handleApply}
              color="primary"
              variant="contained"
              disabled={!preselectedOptions.length}
            >
              Appliquer
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}

const styles = {
  filtersRow: {
    display: "flex",
    gap: "16px",
    marginTop: "0px",
  },
  buttonStyle: {
    textTransform: "none",
    border: "1px solid " + COLORS.BLACK_400,
    color: COLORS.BLACK_600,
    fontWeight: 400,
    fontSize: "14px",
    borderRadius: "4px",
    padding: "8px 12px !important",
    "& svg": {
      marginLeft: "8px",
    },
  },
  paperDialog: {
    position: "fixed",
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    margin: 0,
    borderRadius: "24px 24px 0 0",
    maxWidth: "100% !important",
    minWidth: "300px",
    width: "1024px"
  },
  dialogTitle: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingTop: "22px",
    paddingBottom: "18px",
  },
  dialogTitleCount: {
    marginLeft: "8px",
    aspectRatio: "1 / 1",
    height: "24px",
    backgroundColor: COLORS.PRIMARY_COLOR,
    color: COLORS.WHITE,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    fontWeight: 400,
    borderRadius: "50%",
  },
  DialogContent: {
    padding: "10px 24px 24px 24px",
    display: "flex",
    flexDirection: "column",
    overflowY: "hidden",
  },
  formGroupContainer: {
    marginTop: "16px",
    flex: 1,
    overflowY: "auto",
  },
  iconDelete: {
    position: "absolute",
    right: 8,
    top: 14,
    color: "#555555",
  },
  searchField: {
    backgroundColor: COLORS.BACKGROUND_LIGHT_GREY,
    borderRadius: "8px",
    "& fieldset": {
      border: "none",
      borderRadius: "8px",
    },
  },
  chipStyle: {
    backgroundColor: COLORS.PRIMARY_COLOR,
    color: COLORS.WHITE,
    fontSize: "16px",
    fontWeight: 400,
  },
}

export default TypeFilter
